import React, { useEffect, useState } from "react";
import Axios from "axios";
import {
  antdAsyncMessageError,
  antdAsyncMessageSuccess,
} from "../../../../helpers/AsyncMessage";
import {
  Form,
  Input,
  Button,
  Select,
  Row,
  Col,
  Radio,
  DatePicker,
  Modal,
  Checkbox,
} from "antd";
import ModalSpecial from "../../../Layout/Modals/ModalSpecial";
import { useDispatch } from "react-redux";
import { useMutation, useQuery } from "react-query";
import { setGlobalModal } from "../../../../redux/Actions/orderAction";
import { ButtonDelete } from "../../../common/Buttons";
import { IconEditBold, IconCloseBold } from "../../../Layout/Icons";
import { DebounceSelect } from "../../../common";
import moment from "moment";
import { useSelector } from "react-redux";
import { IconExclamation } from "../../../Layout/Icons";

const { confirm } = Modal;

const CreatePOAssignSupplyModal = ({ orders }) => {
  const [form] = Form.useForm();
  const globalModal = useSelector((state) => state.orders.globalModal);
  const [orderRows, setOrderRows] = useState(
    orders
      .flatMap((order) =>
        order.order_rows.map((orderRow) => ({
          ...orderRow,
          order_shop: order.order_shop,
          product_name:
            order.order_shop.country_id === 122
              ? orderRow.product_name
              : orderRow.product_info?.title,
          ean: orderRow.product_info?.ean || "",
        }))
      )
      .filter(
        (orderRow) =>
          globalModal.numbersArray.includes(orderRow.id) &&
          orderRow?.disabled_at === null
      )
  );
  const [selectedValue, setSelectedValue] = useState("ALSO");
  const [supplierId, setSupplierId] = useState("");
  const [formValues, setFormValues] = useState({});
  const [reviewDate, setReviewDate] = useState(moment());
  const [supplyDate, setSupplyDate] = useState(moment());
  const [disabledDates, setDisabledDates] = useState([]);
  const dispatch = useDispatch();
  const { data: commentStates } = useQuery("commentStates", () =>
    Axios.get(
      "/api/v1/comment_states/search_by_type?[q][comment_type_eq]=comment_management"
    ).then((res) => res.data)
  );

  useEffect(() => {
    const fetchDisabledDates = async () => {
      try {
        const { data } = await Axios.get("/api/v1/calendars");
        setDisabledDates(data.map((date) => moment(date, "YYYY-MM-DD")));
      } catch (err) {
        console.error("Error fetching disabled dates: ", err);
      }
    };

    fetchDisabledDates();
  }, []);

  function transformProductData(product) {
    return {
      order_row_ids: [product.id],
      product_title: product.product_name || "Unknown Product",
      ean: (product.ean || "").trim(),
      quantity: parseFloat(product.quantity) || 0,
    };
  }


  const supplierCodeFetchOptions = async (value) => {
    try {
      let api_url = `/api/v1/suppliers?page=1&per_page=9999999&q[deleted_at_null]=1&q[rivile_supplier_code_null]=0&q[title_or_rivile_supplier_code_i_cont_any]=${value}`;

      const { data } = await Axios.get(api_url);
      const valArr = data?.data;
      return (
        valArr.map((e) => ({
          ...e,
          value: e.id,
          label: `${e.rivile_supplier_code} - ${e.title}`,
        })) || []
      );
    } catch (err) {
      errorHandler(err);
      return [];
    }
  };

  const mutationPOST = useMutation({
    mutationFn: (value) =>
      Axios.post(`api/v1/supplying_incomes/create_po`, {
        products: orderRows.map(transformProductData),
        supplier_id: supplierId,
        client_shipment_date: value.shipment_date
          ? moment(value.shipment_date).format("YYYY-MM-DD")
          : null,
        comment_state_id: value.comment_type,
        comment_text: value.comment,
        warehouse: selectedValue,
        review_date: moment(value.review_date).format("YYYY-MM-DD"),
        supply_date: moment(value.supply_date).format("YYYY-MM-DD"),
        guaranteed_delivery: value.guaranteed_delivery,
      }).then((res) => res.data),
    onSuccess: () => {
      onClose();
      antdAsyncMessageSuccess({ text: "Successfully created", key: "default" });
    },
    onError: antdAsyncMessageError,
  });

  const isWeekend = (date) => {
    const day = date.day();
    return day === 0 || day === 6;
  };

  const disabledDate = (current) => {
    const today = moment().startOf("day");
    return isWeekend(current) || current.isBefore(today);
  };

  const disabledDateReview = (current) => {
    const day = moment(reviewDate).startOf("day");
    return isWeekend(current) || current.isBefore(day);
  };
  const disabledClientShipmentDate = (current) => {
    const day = moment(supplyDate).startOf("day");
    return (
      isWeekend(current) || 
      current.isBefore(day) || 
      disabledDates.some((disabledDate) => current.isSame(disabledDate, "day"))
    );
  };
  const onClose = () => {
    dispatch(setGlobalModal(false, null, null));
  };

  const groupedOrderRows = orderRows.reduce((acc, row) => {
    const { product_info } = row;
    const groupKey = product_info?.id || `orderRow_${row.id}`;
    
    if (!acc[groupKey]) {
      acc[groupKey] = { ...row, quantity: 0 };
    }
    acc[groupKey].quantity += parseFloat(row.quantity) || 0;
    return acc;
  }, {});
  const groupedOrderRowsValues = Object.values(groupedOrderRows);
  const isValidEan13 = (ean) => /^\d{13}$/.test(String(ean || ""));
  const hasInvalidEan = groupedOrderRowsValues.some(
    (item) => !isValidEan13(item.ean)
  );

  const handleProductNameChange = (groupKey, value) => {
    const newOrderRows = orderRows.map((row) => {
      const currentGroupKey = row.product_info?.id || `orderRow_${row.id}`;
      if (currentGroupKey === groupKey) {
        return { ...row, product_name: value };
      }
      return row;
    });
    setOrderRows(newOrderRows);
  };

  const handleEanChange = (groupKey, value) => {
    const sanitizedValue = String(value || "")
      .replace(/\D/g, "")
      .slice(0, 13);

    const newOrderRows = orderRows.map((row) => {
      const currentGroupKey = row.product_info?.id || `orderRow_${row.id}`;
      if (currentGroupKey === groupKey) {
        return { ...row, ean: sanitizedValue };
      }
      return row;
    });
    setOrderRows(newOrderRows);
  };

  const handleQuantityChange = (groupKey, value, id) => {
    const filteredRows = orderRows.filter((row) => {
      const currentGroupKey = row.product_info?.id || `orderRow_${row.id}`;
      return currentGroupKey === groupKey && row.id !== id;
    });
    const totalQuantity = filteredRows.reduce((sum, row) => {
      return sum + parseInt(row.quantity);
    }, 0);
    const newValue = parseInt(value) - totalQuantity;
    console.table({ id, groupKey, value, totalQuantity, newValue });
    const newOrderRows = orderRows.map((row) => {
      if (row.id === id) {
        return { ...row, quantity: newValue };
      }
      return row;
    });
    setOrderRows(newOrderRows);
  };

  const handleDelete = (id) => {
    const index = orderRows.findIndex((row) => row.id === id);
    if (index !== -1) {
      const newOrderRows = [...orderRows];
      newOrderRows.splice(index, 1);
      setOrderRows(newOrderRows);
    }
  };

  const countOrdersWithProductInfo = () => {
    return orders.reduce((count, order) => {
      const validOrderRows = order.order_rows.filter(
        (orderRow) => orderRow.product_info !== null
      );
      return count + validOrderRows.length;
    }, 0);
  };

  const onFinishHandler = (values) => {
    if (hasInvalidEan) {
      antdAsyncMessageError({ text: "EAN must be exactly 13 digits" });
      return;
    }

    const totalOrders = orders.length;
    const totalOrderRowsWithProductInfo = countOrdersWithProductInfo();

    confirm({
      title: "Are you sure?",
      icon: <IconExclamation className="text-primary" />,
      content: `You will not be able to undo this action. Total Orders: ${totalOrders} will be affected, and Products: ${totalOrderRowsWithProductInfo}  will be affected. `,
      onOk() {
        mutationPOST.mutate(values);
      },
      onCancel() {
        console.log("Cancel1");
      },
    });
  };

  const [isEditing, setIsEditing] = useState(
    Array(orderRows.length).fill(false)
  );

  const renderDataTable = () => {
    return (
      <table style={{ backgroundColor: "#FBFBFB" }} className="table">
        <thead>
          <tr>
            <th>SKU</th>
            <th>EAN</th>
            <th>Title</th>
            <th>Qty</th>
          </tr>
        </thead>
        <tbody>
          {groupedOrderRowsValues.map((item, index) => (
            <tr key={index}>
              <td>{item.product_reference}</td>
              <td>
                <Input
                  style={{ width: 130 }}
                  value={item.ean}
                  maxLength={13}
                  inputMode="numeric"
                  onChange={(e) =>
                    handleEanChange(
                      item.product_info?.id || `orderRow_${item.id}`,
                      e.target.value
                    )
                  }
                />
                {!isValidEan13(item.ean) ? (
                  <div className="text-danger">Must be 13 digits</div>
                ) : null}
              </td>
              <td>
                {isEditing[index] ? (
                  <div style={{ width: 180 }} className="d-flex flex-row">
                    <Input.TextArea
                      value={item.product_name}
                      onChange={(e) =>
                        handleProductNameChange(
                          item.product_info?.id || `orderRow_${item.id}`,
                          e.target.value
                        )
                      }
                    />
                    <div
                      className="cursor-pointer"
                      onClick={() => {
                        const newIsEditing = [...isEditing];
                        newIsEditing[index] = false;
                        setIsEditing(newIsEditing);
                      }}
                    >
                      <IconCloseBold />
                    </div>
                  </div>
                ) : (
                  <div style={{ width: 180 }} className="d-flex flex-row">
                    {item.product_name}
                    <div
                      className="cursor-pointer"
                      onClick={() => {
                        const newIsEditing = [...isEditing];
                        newIsEditing[index] = true;
                        setIsEditing(newIsEditing);
                      }}
                    >
                      <IconEditBold />
                    </div>
                  </div>
                )}
              </td>
              <td>
                <div className="d-flex flex-row gap-5">
                  <Input
                    style={{ width: 55 }}
                    value={item.quantity}
                    onChange={(e) =>
                      handleQuantityChange(
                        item.product_info?.id || `orderRow_${item.id}`,
                        e.target.value,
                        item.id
                      )
                    }
                  />
                  <ButtonDelete onDelete={() => handleDelete(item.id)} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <ModalSpecial
      style={{ width: 650 }}
      text={
        <span className="font-bold text-xl text-black">
          Create PO & assign supply
        </span>
      }
      onClose={onClose}
    >
      <Form
        form={form}
        layout="vertical"
        name="basic"
        onFinish={(values) => {
          onFinishHandler(values);
        }}
        onValuesChange={(changedValues, allValues) => {
          setFormValues(allValues);
        }}
        autoComplete="off"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              style={{ marginBottom: 0 }}
              label="Supplier code"
              name="supplier_code"
              rules={[{ required: true, message: "Supplier code is required" }]}
            >
              <DebounceSelect
                labelInValue={false}
                required={true}
                showSearch={true}
                value={supplierId}
                placeholder={"Type to search"}
                fetchOptions={supplierCodeFetchOptions}
                onChange={(value) => {
                  setSupplierId(value);
                }}
                style={{
                  width: "100%",
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              style={{ marginBottom: 0 }}
              label="Arriving to location"
              name="supply_date"
              rules={[{ required: true, message: "Supply date is required" }]}
            >
              <DatePicker
                disabledDate={disabledDateReview}
                style={{ width: "100%" }}
                placeholder="Select"
                onChange={(value) => setSupplyDate(value)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              style={{ marginBottom: 0 }}
              label="Purchase order review date"
              name="review_date"
              rules={[{ required: true, message: "Review date is required" }]}
            >
              <DatePicker
                disabledDate={disabledDate}
                style={{ width: "100% " }}
                placeholder="Select"
                onChange={(value) => setReviewDate(value)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              style={{ marginBottom: 0 }}
              label="Client shipment date"
              name="shipment_date"
            >
              <DatePicker
                disabledDate={disabledClientShipmentDate}
                style={{ width: "100%" }}
                placeholder="Select"
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          style={{ marginBottom: 0 }}
          label="Arrival warehouse"
          name="arrival_warehouse"
        >
          <Row gutter={16}>
            <Col span={6}>
              <div
                className="card-type ant-input cursor-pointer"
                style={{
                  height: 32,
                  borderColor: selectedValue === "ALSO" ? "#03B0F7" : "",
                  backgroundColor: selectedValue === "ALSO" ? "#EDF9FE" : "",
                }}
                onClick={() => setSelectedValue("ALSO")}
              >
                <Radio
                  className={selectedValue === "ALSO" ? "selected-radio" : ""}
                  style={{
                    color: selectedValue === "ALSO" ? "#03B0F7" : "",
                  }}
                  checked={selectedValue === "ALSO"}
                  value="ALSO"
                >
                  ALSO
                </Radio>
              </div>
            </Col>
            <Col span={6}>
              <div
                className="card-type ant-input cursor-pointer"
                style={{
                  height: 32,
                  borderColor: selectedValue === "Office" ? "#03B0F7" : "",
                  backgroundColor: selectedValue === "Office" ? "#EDF9FE" : "",
                }}
                onClick={() => setSelectedValue("Office")}
              >
                <Radio
                  className={selectedValue === "Office" ? "selected-radio" : ""}
                  style={{
                    color: selectedValue === "Office" ? "#03B0F7" : "",
                  }}
                  checked={selectedValue === "Office"}
                  value="Office"
                >
                  Office
                </Radio>
              </div>
            </Col>
            <Col span={6}>
              <div
                className="card-type ant-input cursor-pointer"
                style={{
                  height: 32,
                  borderColor: selectedValue === "Adrem" ? "#03B0F7" : "",
                  backgroundColor: selectedValue === "Adrem" ? "#EDF9FE" : "",
                }}
                onClick={() => setSelectedValue("Adrem")}
              >
                <Radio
                  className={selectedValue === "Adrem" ? "selected-radio" : ""}
                  style={{
                    color: selectedValue === "Adrem" ? "#03B0F7" : "",
                  }}
                  checked={selectedValue === "Adrem"}
                  value="Adrem"
                >
                  Adrem
                </Radio>
              </div>
            </Col>
            <Col span={6}>
              <div
                className="card-type ant-input cursor-pointer"
                style={{
                  height: 32,
                  borderColor: selectedValue === "VLANTANA" ? "#03B0F7" : "",
                  backgroundColor: selectedValue === "VLANTANA" ? "#EDF9FE" : "",
                }}
                onClick={() => setSelectedValue("VLANTANA")}
              >
                <Radio
                  className={selectedValue === "VLANTANA" ? "selected-radio" : ""}
                  style={{
                    color: selectedValue === "VLANTANA" ? "#03B0F7" : "",
                  }}
                  checked={selectedValue === "VLANTANA"}
                  value="VLANTANA"
                >
                  VLANTANA
                </Radio>
              </div>
            </Col>
          </Row>
        </Form.Item>
        <Form.Item
          style={{ marginBottom: 0 }}
          label="Comment type"
          name="comment_type"
          initialValue={4}
        >
          <Select
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {commentStates?.map((commentState) => (
              <Select.Option key={commentState.id} value={commentState.id}>
                {commentState.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="Comment" name="comment">
          <Input.TextArea placeholder="Type in" />
        </Form.Item>
        <hr />
        {renderDataTable()}

        <Form.Item name="guaranteed_delivery" valuePropName="checked">
          <Checkbox>Guaranteed delivery</Checkbox>
        </Form.Item>

        <Form.Item>
          <Button
            disabled={groupedOrderRowsValues.length === 0 || hasInvalidEan}
            className="w-100"
            type="primary"
            htmlType="submit"
            loading={mutationPOST.isLoading}
          >
            Save
          </Button>
        </Form.Item>
      </Form>
    </ModalSpecial>
  );
};

export default CreatePOAssignSupplyModal;
